import styled from "styled-components";
import { useState, useEffect } from "react";
import { Images } from "lucide-react";

import useReducedMotion from "./useReducedMotion";
import Media from "./Media";
import {
  weight,
  type,
  radius,
  motion,
  media,
  color,
} from "../../../styles/tokens";

/* The room's photos, cycling on their own, opening full-screen on click.
 *
 * A room card showed one still, so a guest judging a room saw a single frame of it — usually the
 * bathroom, because that is whatever the CMS happens to list first. Cycling shows the room without
 * asking for a click, and the click is still there for anyone who wants to look properly.
 *
 * Each slide is a real <img> via Media, so the set keeps its srcset and alt text rather than
 * becoming CSS backgrounds.
 */

const SLIDE_MS = 4000;

const Frame = styled.button`
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  min-height: 190px;
  padding: 0;
  border: 0;
  background: ${color.canvasAlt};
  cursor: pointer;
  overflow: hidden;

  ${media.md`
    /* Full-bleed band above the detail on a phone, where a side-by-side image would be a stamp. */
    aspect-ratio: 16 / 10;
    min-height: 0;
  `}
`;

const Slide = styled.span`
  position: absolute;
  inset: 0;
  opacity: ${(props) => (props.$active ? 1 : 0)};
  transition: opacity 900ms ease-in-out;

  > div {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
`;

/* Sits over the image, so it reads as "there are more of these" rather than as a separate control. */
const Badge = styled.span`
  position: absolute;
  right: 10px;
  bottom: 10px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 11px;
  border-radius: ${radius.pill};
  background: rgba(20, 21, 15, 0.66);
  color: ${color.onDark};
  font-size: ${type.xs};
  font-weight: ${weight.semibold};
  letter-spacing: 0.04em;
  backdrop-filter: blur(4px);
  transition: background ${motion.base};

  ${Frame}:hover & {
    background: rgba(20, 21, 15, 0.85);
  }
`;

const Dots = styled.span`
  position: absolute;
  left: 10px;
  bottom: 14px;
  z-index: 1;
  display: flex;
  gap: 4px;
`;

const Dot = styled.span`
  width: ${(props) => (props.$active ? "14px" : "5px")};
  height: 5px;
  border-radius: ${radius.pill};
  background: ${(props) => (props.$active ? color.onDark : "rgba(246, 244, 238, 0.5)")};
  transition:
    width ${motion.base},
    background ${motion.base};
`;

const RoomGallery = (props) => {
  const { images, alt, sizes, photosLabel, viewLabel, onOpen } = props;
  const reducedMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const count = images.length;

  useEffect(() => {
    if (reducedMotion || paused || count <= 1) return undefined;

    const timer = setInterval(
      () => setIndex((current) => (current + 1) % count),
      SLIDE_MS,
    );
    return () => clearInterval(timer);
  }, [reducedMotion, paused, count]);

  if (count === 0) return null;

  return (
    <Frame
      type="button"
      aria-label={viewLabel}
      onClick={() => onOpen(0)}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {images.map((image, position) => (
        <Slide
          key={image.url}
          $active={position === index}
          aria-hidden={position !== index}
        >
          <Media
            source={image}
            alt={position === 0 ? alt : ""}
            sizes={sizes}
            priority={position === 0}
          />
        </Slide>
      ))}

      {count > 1 ? (
        <Dots aria-hidden="true">
          {images.slice(0, 6).map((image, position) => (
            <Dot key={image.url} $active={position === index} />
          ))}
        </Dots>
      ) : null}

      <Badge>
        <Images size={13} aria-hidden="true" />
        {photosLabel.replace("%{n}", count)}
      </Badge>
    </Frame>
  );
};

export default RoomGallery;
